import { Id, Resource, User } from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import {
  THUMBNAIL_IMAGE_MARGIN_HORIZONTAL,
  THUMBNAIL_IMAGE_WIDTH,
} from 'chat/messages/views/thumbnail_image'
import { BUBBLE_AVATAR_SIZE } from 'chat/shared/constants'
import {
  FileListItem,
  FolderListItem,
} from 'file_folder_repository/views/list_item'
import moment from 'moment'
import { useCallback, useMemo } from 'react'
import { FlatList, Platform, useWindowDimensions, View } from 'react-native'
import {
  AvatarProps,
  Bubble,
  BubbleProps,
  GiftedChat,
  utils as giftedChatUtils,
  IMessage,
  MessageImageProps,
  Time,
} from 'react-native-gifted-chat'
import { NonSelectableText } from 'shared/components/chat/non_selectable_text'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import { getDarkShadeColor } from 'shared/utils'

interface Props {
  chatGroupFileFolderList: Resource[] | undefined
  onPress(resource: Resource): void
  onLongPressResource(resource: Resource): void
  user: User | undefined
}

export function FileFolderTimelineView({
  chatGroupFileFolderList,
  user,
  onLongPressResource,
  onPress,
}: Props) {
  const styles = useStyles()
  const { groupedFileFolders, messageIdToGroupIndex } = useMemo(
    () => groupFileFolders(chatGroupFileFolderList),
    [chatGroupFileFolderList]
  )

  const chatMessages: IMessage[] = useMemo(
    () => groupedFileFolders.map(group => convertGroupToChatMessage(group)),
    [groupedFileFolders]
  )

  const getMessageGroup = useCallback(
    (id?: Id) => {
      if (id && messageIdToGroupIndex.has(id)) {
        const groupIndex = messageIdToGroupIndex.get(id)
        if (groupIndex !== undefined) {
          return groupedFileFolders[groupIndex]
        }
      }
      return undefined
    },
    [groupedFileFolders, messageIdToGroupIndex]
  )

  const renderMessageImage = useCallback(
    (props: MessageImageProps<IMessage>) => {
      const fileGroup = getMessageGroup(props.currentMessage?._id.toString())

      if (!fileGroup) {
        return null
      }
      const isSender = props.currentMessage?.user._id === user?.id

      return (
        <View style={styles.messageContainer}>
          <FlatList
            data={fileGroup}
            horizontal={true}
            keyExtractor={item => item.id.toString()}
            contentContainerStyle={styles.documentRowContainer}
            renderItem={({ item }) => (
              <View>
                {item.type === 'file' ? (
                  <FileListItem
                    file={item}
                    onPress={onPress}
                    onLongPress={onLongPressResource}
                    style={styles.fileFolderListItemStyle}
                    titleStyle={
                      isSender
                        ? styles.fileFolderListItemTextSelf
                        : styles.fileFolderListItemTextOther
                    }
                    descriptionStyle={
                      isSender
                        ? styles.fileFolderListItemTextSelf
                        : styles.fileFolderListItemTextOther
                    }
                    infoContainerStyle={styles.fileFolderListItemContainer}
                    thumbnailHeight={100}
                    thumbnailWidth={80}
                    thumbnailIconSize={50}
                    metaDataType="sizeDesc"
                  />
                ) : (
                  <FolderListItem
                    folder={item}
                    onPress={onPress}
                    onLongPress={onLongPressResource}
                    style={styles.fileFolderListItemStyle}
                    titleStyle={
                      isSender
                        ? styles.fileFolderListItemTextSelf
                        : styles.fileFolderListItemTextOther
                    }
                    descriptionStyle={
                      isSender
                        ? styles.fileFolderListItemTextSelf
                        : styles.fileFolderListItemTextOther
                    }
                    infoContainerStyle={styles.fileFolderListItemContainer}
                    thumbnailHeight={100}
                    thumbnailWidth={80}
                    thumbnailIconSize={50}
                    metaDataType="sizeDesc"
                  />
                )}
              </View>
            )}
          />
        </View>
      )
    },
    [
      getMessageGroup,
      onLongPressResource,
      onPress,
      styles.documentRowContainer,
      styles.fileFolderListItemContainer,
      styles.fileFolderListItemStyle,
      styles.fileFolderListItemTextOther,
      styles.fileFolderListItemTextSelf,
      styles.messageContainer,
      user?.id,
    ]
  )

  const renderBubble = useCallback(
    (props: BubbleProps<IMessage>) => {
      const messageGroup = getMessageGroup(props.currentMessage?._id.toString())
      if (!props.currentMessage || !messageGroup) {
        return null
      }

      const chatMessage: IMessage = props.currentMessage

      const isNotSameUser = !giftedChatUtils.isSameUser(
        chatMessage,
        props.previousMessage
      )
      const isNotSameDay = !giftedChatUtils.isSameDay(
        chatMessage,
        props.previousMessage
      )
      const isReceived = chatMessage.user._id !== user?.id
      const showName = (isNotSameUser || isNotSameDay) && isReceived

      const bubbleProps = props

      return (
        <MessageBubble
          bubbleProps={bubbleProps}
          documentGroup={messageGroup}
          showName={showName}
        />
      )
    },
    [getMessageGroup, user?.id]
  )

  const renderTime = useCallback(
    (props: any) => {
      return (
        <Time
          {...props}
          timeTextStyle={{
            left: styles.otherTimeText,
            right: styles.selfTimeText,
          }}
        />
      )
    },
    [styles.otherTimeText, styles.selfTimeText]
  )

  const renderAvatar = useCallback(
    (props: AvatarProps<IMessage>) => {
      const { currentMessage: chatMessage } = props

      if (!chatMessage) {
        return null
      }
      const messageUser = getDocumentUser(
        getMessageGroup(chatMessage._id.toString())?.[0]
      )

      return (
        <UserAvatar
          user={messageUser}
          showAvatarIfNoPhone={true}
          size={BUBBLE_AVATAR_SIZE}
        />
      )
    },
    [getMessageGroup]
  )

  const renderEmptyFooter = useCallback(() => {
    return <View style={styles.emptyFooter} />
  }, [styles.emptyFooter])

  if (!user) {
    return null
  }

  return (
    <View style={styles.container}>
      <GiftedChat
        messages={chatMessages}
        listProps={{
          scrollIndicatorInsets: { right: 1 },
        }}
        renderInputToolbar={() => null}
        renderBubble={renderBubble}
        renderMessageImage={renderMessageImage}
        renderTime={renderTime}
        renderAvatar={renderAvatar}
        minInputToolbarHeight={0}
        minComposerHeight={0}
        renderFooter={renderEmptyFooter}
        onLongPressMessage={() => null}
        user={{
          _id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          avatar: user.photoUrl ?? undefined,
        }}
        renderChatEmpty={() => (
          <View style={styles.emptyComponent}>
            <Text style={styles.emptyComponentText}>
              {t('noFilesOrFoldersFound')}
            </Text>
          </View>
        )}
      />
    </View>
  )
}

interface MessageBubbleProps {
  documentGroup: Resource[] | undefined
  bubbleProps: BubbleProps<IMessage>
  showName: boolean
}

function MessageBubble({
  documentGroup,
  bubbleProps,
  showName,
}: MessageBubbleProps) {
  const styles = useStyles()
  const dimensions = useWindowDimensions()
  if (!documentGroup || documentGroup.length === 0) {
    return null
  }

  const messageUser = getDocumentUser(documentGroup[0])

  if (Platform.OS === 'web') {
    const width =
      WEB_SCREEN_WIDTH < dimensions.width
        ? WEB_SCREEN_WIDTH - 100
        : dimensions.width - 100
    bubbleProps['wrapperStyle'] = {
      left: {
        maxWidth: width,
      },
      right: {
        maxWidth: width,
      },
    }
  }

  return (
    <View
      onContextMenu={event => {
        if (bubbleProps.onLongPressMessage) {
          event.preventDefault()
          bubbleProps.onLongPressMessage(null, bubbleProps.currentMessage)
        }
      }}
    >
      {showName && messageUser ? (
        <NonSelectableText style={styles.name}>
          {`${messageUser.firstName} ${messageUser.lastName}`}
        </NonSelectableText>
      ) : null}
      <Bubble
        {...bubbleProps}
        textStyle={{
          right: [bubbleProps.textStyle?.right, styles.selfTextStyle],
          left: [bubbleProps.textStyle?.left, styles.otherTextStyle],
        }}
        wrapperStyle={{
          left: [bubbleProps.wrapperStyle?.left, styles.otherBubbleContainer],
          right: [bubbleProps.wrapperStyle?.right, styles.selfBubbleContainer],
        }}
        containerStyle={{
          left: { alignItems: 'stretch' },
          right: { alignItems: 'stretch' },
        }}
      />
    </View>
  )
}

const convertGroupToChatMessage = (group: Resource[]): IMessage => {
  const firstFile = group[0]
  const messageBody = ''
  const user = getDocumentUser(firstFile)
  return {
    _id: firstFile.id,
    text: messageBody,
    createdAt: moment(firstFile.created).toDate(),
    image: 'image',
    user: {
      _id: user?.id ?? '0',
      name: `${user?.firstName} ${user?.lastName}`,
      avatar: user?.photoUrl,
    },
  }
}

interface GroupedFileFoldersResut {
  groupedFileFolders: Resource[][]
  messageIdToGroupIndex: Map<Id, number>
}

const getDocumentUser = (document: Resource | undefined): User | undefined => {
  return document?.type === 'file'
    ? document.currentVersion?.uploadedBy
    : document?.createdBy
}

const groupFileFolders = (
  resources: Resource[] | undefined
): GroupedFileFoldersResut => {
  const groupedFiles: Resource[][] = []
  const messageIdToGroupIndex = new Map<Id, number>()
  const processedResourceIds = new Set<Id>()
  let currentGroup: Resource[] = []

  resources?.sort((a, b) => (b.created ?? 0) - (a.created ?? 0))
  resources?.forEach((document, index) => {
    if (processedResourceIds.has(document.id)) {
      return
    }

    const currentDocumentUserId = getDocumentUser(document)?.id
    const prevDocumentUserId = getDocumentUser(resources[index - 1])?.id
    const isWithinOneHour =
      index > 0 &&
      Math.abs(
        moment(document.created).diff(
          moment(resources[index - 1].created),
          'hours'
        )
      ) < 1

    if (
      index === 0 ||
      currentDocumentUserId !== prevDocumentUserId ||
      !isWithinOneHour
    ) {
      if (currentGroup.length > 0) {
        groupedFiles.push(currentGroup)
        currentGroup.forEach(doc => {
          messageIdToGroupIndex.set(doc.id.toString(), groupedFiles.length - 1)
        })
      }
      currentGroup = [document]
    } else {
      currentGroup.push(document)
    }
    processedResourceIds.add(document.id)
  })

  if (currentGroup.length > 0) {
    groupedFiles.push(currentGroup)
    currentGroup.forEach(resource => {
      messageIdToGroupIndex.set(resource.id.toString(), groupedFiles.length - 1)
    })
  }

  return { groupedFileFolders: groupedFiles, messageIdToGroupIndex }
}

export const useStyles = makeStyles(theme => ({
  name: {
    color: theme.mode === 'dark' ? '#BBBBBB' : '#888888',
    fontSize: 12,
    paddingBottom: 5,
    paddingLeft: 10,
  },
  messageImage: {
    width: 200,
    height: 200,
    marginTop: 7,
    borderRadius: 8,
  },
  videoIcon: {
    height: 30,
    width: 30,
  },
  multipleImagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: THUMBNAIL_IMAGE_WIDTH + 2 * THUMBNAIL_IMAGE_MARGIN_HORIZONTAL,
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  multipleFileFolderssContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 3,
    paddingHorizontal: 5,
  },
  thumbnailOverlay: {
    position: 'absolute',
    zIndex: 999,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  videoIconOverlay: {
    width: 200,
    height: 200,
    marginTop: 7,
  },
  emptyMessageImage: {
    width: 200,
    height: 200,
    borderRadius: 8,
    marginHorizontal: 5,
    marginTop: 10,
  },
  countOverlay: {
    width: 115,
    height: 115,
    marginTop: 7,
  },
  fullImage: {
    width: THUMBNAIL_IMAGE_WIDTH,
    height: THUMBNAIL_IMAGE_WIDTH,
  },
  halfImage: {
    width: THUMBNAIL_IMAGE_WIDTH,
    height: THUMBNAIL_IMAGE_WIDTH / 2,
  },
  quarterImage: {
    width: THUMBNAIL_IMAGE_WIDTH / 2,
    height: THUMBNAIL_IMAGE_WIDTH / 2,
  },
  countText: {
    fontSize: 30,
    color: 'white',
    fontWeight: 'bold',
  },
  documentNameContainer: {
    position: 'absolute',
    bottom: 0,
    backgroundColor:
      theme.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.7)',
    width: '100%',
    borderBottomStartRadius: 8,
    borderBottomEndRadius: 8,
    padding: 5,
  },
  attachmentsLabel: {
    marginLeft: 10,
    marginTop: 10,
    color: theme.colors.grey9,
    textTransform: 'uppercase',
  },
  attachmentsContainer: { justifyContent: 'center' },
  selfBubbleContainer: {
    backgroundColor: theme.colors.yellow,
  },
  otherBubbleContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.grey6,
  },
  selfTextStyle: {
    fontWeight: 'normal',
    color: 'black',
  },
  otherTextStyle: { fontWeight: 'normal', color: theme.colors.black },
  selfLinkText: { color: 'black' },
  otherLinkText: { color: theme.colors.black },
  selfBubbleMentions: { color: 'black', fontWeight: 'bold', fontSize: 12 },
  otherBubbleMentions: {
    color: theme.colors.black,
    fontWeight: 'bold',
    fontSize: 12,
  },
  mentionsContainer: { paddingHorizontal: 10 },
  container: {
    flex: 1,
    justifyContent: 'center',
  },
  descriptionBox: {
    flexDirection: 'row',
    backgroundColor: '#FFFFB7',
  },
  descriptionText: {
    flex: 1,
    color: '#4E5665',
    paddingTop: 10,
    paddingStart: 15,
    paddingBottom: 10,
    fontSize: 13,
  },
  descriptionCloseButton: {
    width: 25,
    height: 25,
    paddingTop: 5,
    textAlign: 'center',
    fontSize: 15,
    fontFamily: font.bold,
    color: theme.colors?.grey3,
  },
  errorMessage: {
    fontSize: 17,
    textAlign: 'center',
  },
  messageLoadingIndicator: {
    flex: 1,
  },

  offlineText: {
    padding: 10,
    fontSize: 15,
    fontStyle: 'italic',
    textAlign: 'center',
    textAlignVertical: 'center',
  },
  pinnedMessagesHeader: {
    height: 50,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 15,
    padding: 10,
    paddingRight: 20,
    backgroundColor: theme.mode === 'dark' ? '#121212' : '#f9f9f9',
    borderBottomColor: 'blue',
    borderBottomWidth: 0,
  },
  pinnedMessagesHeaderText: {
    flex: 1,
    color: theme.colors?.primary,
    fontSize: 20,
    fontFamily: font.bold,
    marginLeft: 5,
  },
  pinnedChatMessages: {
    zIndex: 10,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors?.grey6 : theme.colors.teal,
  },
  emptyListContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    transform: Platform.OS === 'android' ? [{ scale: -1 }] : [{ scaleY: -1 }],
  },
  pinnedButtonContainer: { zIndex: 10 },
  pinnedMessagesModal: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  pinnedMessagesModalContainer: {
    flex: 1,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { maxWidth: WEB_SCREEN_WIDTH }),
  },
  emptyFooter: { height: 70 },
  selfTimeText: { color: theme.colors?.grey9 },
  otherTimeText: {
    color: theme.mode === 'dark' ? theme.colors.grey1 : theme.colors?.grey9,
  },
  messageContainer: {
    borderRadius: 15,
    overflow: 'hidden',
    paddingTop: 5,
  },
  fileFolderListItemContainer: { maxWidth: 175 },
  fileFolderListItemTextOther: { color: theme.colors?.black },
  fileFolderListItemTextSelf: { color: 'black' },
  fileFolderListItemStyle: {
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor(0.1) : getDarkShadeColor(0.05),
  },
  documentRowContainer: {
    flexGrow: 1,
  },
  emptyComponent: {
    flex: 1,
    justifyContent: 'center',
    transform: Platform.OS === 'android' ? [{ scale: -1 }] : [{ scaleY: -1 }],
  },
  emptyComponentText: { textAlign: 'center' },
}))
